"use no memo";

import { ProductCategorySchema } from "@repo/validators";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { ProductCategory } from "~/providers/product-provider";
import { useCourse } from "~/hooks/use-course";
import { useGeneral } from "~/hooks/use-general";
import { usePrinter } from "~/hooks/use-printer";
import { useProduct } from "~/hooks/use-product";
import { useProductCategoryManager } from "~/managers/use-product-category-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { ColorPicker } from "../ui/color-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { MultiSelect } from "../ui/multi-select";

const createPositionsWithEmptyValues = (
  categories: ProductCategory[],
  numEmpty: number,
) => {
  const initialPositions = categories
    .sort((a, b) => a.position - b.position)
    .map((category) => ({
      label: `${category.name} (${category.position})`,
      value: category.position,
    }));

  const filledPositions = initialPositions.map((item) => item.value);

  const maxPosition = filledPositions[filledPositions.length - 1] ?? 0;

  // Generate empty positions between existing positions
  const emptyPositionsBetween = [];
  for (let i = 1; i <= maxPosition; i++) {
    if (!filledPositions.includes(i)) {
      emptyPositionsBetween.push({ label: `${i}`, value: i });
    }
  }

  // Generate additional empty positions after the last position
  const emptyPositionsAfter = Array.from({ length: numEmpty }, (_, i) => ({
    label: `${maxPosition + i + 1}`,
    value: maxPosition + i + 1,
  }));

  return [
    ...initialPositions,
    ...emptyPositionsBetween,
    ...emptyPositionsAfter,
  ];
};

export default function ProductCategoryForm({
  productCategory,
  onClose,
}: {
  productCategory?: ProductCategory;
  onClose: (force?: boolean) => void;
}) {
  const { t, i18n } = useTranslation("common");

  const { courses } = useCourse();

  const { vatRates, turnoverGroups } = useGeneral();

  const { modifierGroups, productCategories } = useProduct();

  const { printRoutes } = usePrinter();

  const { createProductCategory, updateProductCategory, isLoading } =
    useProductCategoryManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const positions = createPositionsWithEmptyValues(productCategories, 10).sort(
    (a, b) => a.value - b.value,
  );

  const firstAvailablePosition = positions.find(
    (p) => !productCategories.find((pc) => pc.position === p.value),
  )?.value;

  const form = useForm({
    reValidateMode: "onChange",
    schema: ProductCategorySchema(i18n),
    defaultValues: {
      name: productCategory?.name ?? "",
      color: productCategory?.color ?? "",
      position: productCategory?.position ?? firstAvailablePosition,
      modifierGroupIds: productCategory?.modifierGroupIds ?? [],
      vatRateId: productCategory?.vatRateId ?? "",
      printRouteId: productCategory?.printRouteId ?? "",
      notifyPrintRouteId: productCategory?.notifyPrintRouteId ?? "",
      courseId: productCategory?.courseId ?? "",
      turnoverGroupId: productCategory?.turnoverGroupId ?? "",
    },
  });

  const printRouteId = useWatch({
    control: form.control,
    name: "printRouteId",
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (productCategory) {
      const { data, error } = await updateProductCategory({
        id: productCategory.id,
        ...formData,
      });
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const result = await createProductCategory(formData);
      if (result) {
        form.reset();
        onClose(true);
      }
    }
  });

  const modifierGroupOptions = [
    ...modifierGroups.map((group) => ({
      value: group.id,
      label: group.name,
    })),
  ];

  return (
    <Form {...form}>
      <form
        id="product-category-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-4"
      >
        <FormField
          disabled={isLoading}
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("ProductCategory.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.position")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select position..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={positions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.color")}</FormLabel>
              <FormControl>
                <ColorPicker {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="courseId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.course")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select a default order chart..."
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={courses.map((course) => ({
                    value: course.id,
                    label: course.name,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="vatRateId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.vat_rate")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("ProductCategory.vat_rate_placeholder")}
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={vatRates.map((vatRate) => ({
                    value: vatRate.id,
                    label: vatRate.name,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="turnoverGroupId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.turnover_group")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("ProductCategory.turnover_group_placeholder")}
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={turnoverGroups.map((turnoverGroup) => ({
                    value: turnoverGroup.id,
                    label: turnoverGroup.name,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="printRouteId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.print_route")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select a default print route..."
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0] ?? "");
                  }}
                  options={printRoutes.map((printRoute) => ({
                    value: printRoute.id,
                    label: printRoute.name,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          disabled={isLoading}
          control={form.control}
          name="notifyPrintRouteId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.notify_print_route")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0] ?? "");
                  }}
                  options={printRoutes
                    .filter((p) => p.id !== printRouteId)
                    .map((printRoute) => ({
                      value: printRoute.id,
                      label: printRoute.name,
                    }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormDescription>
                {t("ProductCategory.notify_print_route_description")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="modifierGroupIds"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.modifier_groups")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("ProductCategory.modifier_groups_placeholder")}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={modifierGroupOptions}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
